import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import api from "../Api";
import TableFooter from "../components/TableFooter";
import Popup from "../components/PopUP";
import DeleteModal from "../components/DeleteModal";
import ActivateModal from "../components/ActivateModal";
import DeactivateModal from "../components/DeactivateModal";
import { Link } from "react-router-dom";
import { IMAGE_URL } from "../utils/constants";

const PAGE_SIZE = 20;
const GLOSSARY_URL = "/user-service/user/glossary"; // Adjust if your base API is different

export default function GlossaryManagement() {
    const [glossaries, setGlossaries] = useState({});
    const [loading, setLoading] = useState(true);
    const [popupMessage, setPopupMessage] = useState("");

    const [pagination, setPagination] = useState({
        count: 0,
        total_pages: 0,
        next: 0,
        previous: 0,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [query, setQuery] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // Modals State
    const [showModal, setShowModal] = useState(false);
    const [showActiveModal, setShowActiveModal] = useState(false);
    const [showDeactiveModal, setShowDeactiveModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Debounce search -> backend `q`
    useEffect(() => {
        const t = setTimeout(() => {
            setCurrentPage(1);
            setQuery(searchInput.trim());
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (popupMessage === "") {
            setPopupMessage(
                loading ? "Glossary loading, please wait ..." : "Glossary loaded successfully"
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // Fetch Data (Wrapped in useCallback so we can trigger it manually after actions)
    const fetchData = useCallback(async () => {
        let cancelled = false;
        setLoading(true);
        try {
            const response = await api.get(
                `${GLOSSARY_URL}/?page=${currentPage}&q=${encodeURIComponent(query)}`
            );
            if (cancelled) return;

            const data = response.data;
            setGlossaries(data.results || {});
            setPagination({
                count: data.count ?? 0,
                total_pages: data.total_pages ?? 0,
                next: data.next ?? 0,
                previous: data.previous ?? 0,
            });
        } catch (error) {
            if (cancelled) return;
            console.error("Glossary API Error:", error.message);
            setPopupMessage("Failed to load glossary data");
        }
        if (!cancelled) setLoading(false);
        return () => { cancelled = true; };
    }, [currentPage, query]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Flatten grouped results
    const groupedEntries = Object.entries(glossaries);
    const flatItems = groupedEntries.flatMap(([letter, items]) => items || []);

    // --- ACTION HANDLERS ---

    // 1. Delete
    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedItem) return;
        setLoading(true);
        try {
            // DELETE /glossary?glossary_id=UUID
            const response = await api.delete(`${GLOSSARY_URL}?glossary_id=${selectedItem.glossary_id}`);
            setPopupMessage(response.data?.detail || "Glossary deleted successfully");
            fetchData(); // Refresh table
        } catch (error) {
            console.error("Delete error:", error);
            setPopupMessage(error.response?.data?.detail || "An error occurred while deleting");
        }
        setShowModal(false);
        setSelectedItem(null);
    };

    // 2. Activate
    const handleActivateClick = (item) => {
        setSelectedItem(item);
        setShowActiveModal(true);
    };

    const handleConfirmActivation = async () => {
        if (!selectedItem) return;
        setLoading(true);
        try {
            // PATCH /glossary body: { glossary_id, action }
            const response = await api.patch(`${GLOSSARY_URL}`, {
                glossary_id: selectedItem.glossary_id,
                action: "activate"
            });
            setPopupMessage(response.data?.details || "Glossary activated successfully");
            fetchData();
        } catch (error) {
            console.error("Activation error:", error);
            setPopupMessage(error.response?.data?.detail || "An error occurred during activation");
        }
        setShowActiveModal(false);
        setSelectedItem(null);
    };

    // 3. Deactivate
    const handleDeactivateClick = (item) => {
        setSelectedItem(item);
        setShowDeactiveModal(true);
    };

    const handleConfirmDeactivateClick = async () => {
        if (!selectedItem) return;
        setLoading(true);
        try {
            // PATCH /glossary body: { glossary_id, action }
            const response = await api.patch(`${GLOSSARY_URL}`, {
                glossary_id: selectedItem.glossary_id,
                action: "deactivate"
            });
            setPopupMessage(response.data?.details || "Glossary deactivated successfully");
            fetchData();
        } catch (error) {
            console.error("Deactivation error:", error);
            setPopupMessage(error.response?.data?.detail || "An error occurred during deactivation");
        }
        setShowDeactiveModal(false);
        setSelectedItem(null);
    };

    return (
        <div className="user-management-page">
            <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
            <Sidebar />

            <div className="main-wrapper">
                <Header />

                <div className="content-wrapper">
                    <section className="main-section spacer-y">
                        <div className="container">
                            <div className="row gy-4">
                                <div className="col-xl-12">

                                    {/* Heading */}
                                    <div className="common_main_bg_wrapper">
                                        <div className="common_main_heading_wrapper">
                                            <div className="vstack">
                                                <h1>Glossary Management</h1>
                                            </div>
                                            <div className="d-flex flex-end">
                                                <Link to="/upload-glossary" className="secondary_btn">Upload CSV</Link>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="common-table-wrapper mt-4">
                                        {/* Filters */}
                                        <div className="common-table-filter-wrapper">
                                            <div className="common-left-blk">
                                                <div className="common_sub_heading_wrapper mb-0">
                                                    <h2>
                                                        Glossary Listing{" "}
                                                       
                                                    </h2>
                                                </div>
                                            </div>
                                            <div className="common-right-blk">
                                                <div className="common-sort-blk">
                                                    <div className="common-search-blk">
                                                        <input
                                                            type="search"
                                                            className="form-control"
                                                            id="search"
                                                            placeholder="Search glossary"
                                                            value={searchInput}
                                                            onChange={(e) => setSearchInput(e.target.value)}
                                                            name="glossary_search"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Table Area */}
                                        {loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ minHeight: "300px" }}
                                            >
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="table-blk table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>S.No.</th>
                                                                <th>Title</th>
                                                                <th>Description</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {flatItems.length > 0 ? (
                                                                flatItems.map((item, index) => (
                                                                    <tr key={`${item.title}-${index}`}>
                                                                        <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                                                                        <td className="text-capitalize">
                                                                            {item.title || "-"}
                                                                        </td>
                                                                        <td>{item.description || "-"}</td>
                                                                        
                                                                        {/* Action Buttons - Fixed to be in one line */}
                                                                        <td className="align-middle">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                {/* Deactivate / Activate Button */}
                                                                                {item.is_active === false ? (
                                                                                    <button
                                                                                        className="border-0 bg-transparent p-0"
                                                                                        title="Activate"
                                                                                        onClick={() => handleActivateClick({ "glossary_id": item.id, "name": item.title || "-" })}
                                                                                    >
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" className="bi bi-toggle-off" viewBox="0 0 16 16">
                                                                                            <path d="M11 4a4 4 0 0 1 0 8H8a5 5 0 0 0 2-4 5 5 0 0 0-2-4zm-6 8a4 4 0 1 1 0-8 4 4 0 0 1 0 8M0 8a5 5 0 0 0 5 5h6a5 5 0 0 0 0-10H5a5 5 0 0 0-5 5"/>
                                                                                        </svg>
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        className="border-0 bg-transparent p-0"
                                                                                        title="Deactivate"
                                                                                        onClick={() => handleDeactivateClick({ "glossary_id": item.id, "name": item.title || "-" })}
                                                                                    >
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" className="bi bi-toggle-on" viewBox="0 0 16 16">
                                                                                            <path d="M5 3a5 5 0 0 0 0 10h6a5 5 0 0 0 0-10zm6 9a4 4 0 1 1 0-8 4 4 0 0 1 0 8" />
                                                                                        </svg>
                                                                                    </button>
                                                                                )}

                                                                                {/* Delete Button */}
                                                                                <button
                                                                                    className="border-0 bg-transparent p-0"
                                                                                    title="Delete"
                                                                                    onClick={() => handleDeleteClick({ "glossary_id": item.id, "name": item.title || "-" })}
                                                                                >
                                                                                    <img src={IMAGE_URL + "trash-icon.svg"} alt="Trash" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center text-muted">
                                                                        No glossary records found
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Backend-driven pagination */}
                                                <TableFooter
                                                    pagination={pagination}
                                                    currentPage={currentPage}
                                                    setCurrentPage={setCurrentPage}
                                                />
                                            </>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Modals */}
            <DeleteModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleConfirmDelete}
                itemName={selectedItem ? selectedItem.name : ""}
            />
            <ActivateModal
                show={showActiveModal}
                onClose={() => setShowActiveModal(false)}
                onConfirm={handleConfirmActivation}
                itemName={selectedItem ? selectedItem.name : ""}
            />
            <DeactivateModal
                show={showDeactiveModal}
                onClose={() => setShowDeactiveModal(false)}
                onConfirm={handleConfirmDeactivateClick}
                itemName={selectedItem ? selectedItem.name : ""}
            />
        </div>
    );
}